import React from "react";
import PropTypes from "prop-types";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { OrbPill } from "./orb";
import { isChunkLoadError, attemptRecoveryReload } from "../util/chunkRecovery";

// Real, visible loading/error surfaces. Before these existed every Suspense
// fallback and router fallbackElement rendered literally nothing, so any slow
// or failed chunk load looked like a dead blank page.

const cardStyle = {
  fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  maxWidth: 440,
  margin: "18vh auto",
  padding: "28px 32px",
  border: "1px solid #e8e6df",
  borderRadius: 16,
  background: "#fff",
  textAlign: "center",
  boxShadow: "0 6px 24px rgba(0,0,0,.05)",
};

// The route-level fallback. This module is in the ENTRY chunk, so importing the
// orb here is what puts it in the initial bundle — the first loading screen
// must never wait on a lazily fetched chunk to draw itself.
export const AppLoading = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
    <OrbPill label="Loading…" />
  </div>
);

export const AppErrorScreen = ({ error }) => (
  <div style={cardStyle}>
    <h2 style={{ margin: "0 0 8px", color: "#1d1c19" }}>Something went wrong</h2>
    <p style={{ color: "#6f6f68", margin: "0 0 18px" }}>
      This usually happens right after an update. Refreshing normally fixes it.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      style={{ background: "#4263eb", color: "#fff", border: 0, borderRadius: 8, padding: "9px 24px", fontWeight: 600, cursor: "pointer" }}
    >
      Refresh
    </button>
    {error?.message ? (
      <div style={{ marginTop: 16, fontSize: 11.5, color: "#b0aea6", wordBreak: "break-word" }}>{String(error.message).slice(0, 200)}</div>
    ) : null}
  </div>
);
AppErrorScreen.propTypes = { error: PropTypes.any };

// errorElement for the routers — react-router funnels route/lazy errors here
// instead of its default "Unexpected Application Error!" text.
export const RouteErrorScreen = () => {
  const error = useRouteError();
  if (isChunkLoadError(error) && attemptRecoveryReload()) return null; // overlay + reload in flight
  // A router miss is not a broken update — say so instead of asking for a
  // refresh that can't help. (The storefront catch-alls normally render a
  // store-branded 404 before this is ever reached.)
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 8px", color: "#1d1c19" }}>This page doesn&apos;t exist</h2>
        <p style={{ color: "#6f6f68", margin: "0 0 18px" }}>The link may be broken, or the page may have been moved.</p>
        <a href="/" style={{ display: "inline-block", background: "#4263eb", color: "#fff", borderRadius: 8, padding: "9px 24px", fontWeight: 600, textDecoration: "none" }}>
          Return to homepage
        </a>
      </div>
    );
  }
  return <AppErrorScreen error={error} />;
};

// Root boundary — catches errors from React.lazy trees rendered OUTSIDE any
// router (Login / EnvironmentLogin), which previously unmounted to a blank
// page.
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && attemptRecoveryReload()) {
      this.setState({ recovering: true }); // overlay shown; page being replaced
    }
  }

  render() {
    if (this.state.recovering) return null;
    if (this.state.error) return <AppErrorScreen error={this.state.error} />;
    return this.props.children;
  }
}
AppErrorBoundary.propTypes = { children: PropTypes.node };
